import { Button, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  Cube as CubeIcon,
  FileArrowDown as FileArrowDownIcon,
  FilePdf as FilePdfIcon,
  PaperPlaneTilt as PaperPlaneIcon,
  Users as UsersIcon,
} from "@phosphor-icons/react";
import { captureException } from "@sentry/react";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useEffectEvent,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";
import {
  BimModelFilterBar,
  type BimModelListFilters,
  bimModelFilterPreferences,
  bimModelFiltersToSearchParams,
  filterBimModelTree,
  hasActiveBimModelFilters,
  readBimModelFilters,
} from "../components/bim-model-filter-bar";
import {
  AwaitingDiffusionLabel,
  DiffusionDialog,
  type DiffusionTarget,
} from "../components/document-table/diffusion-dialog";
import { maquetteVersionLabel } from "../components/document-table/document-table";
import { openFichier } from "../components/document-table/open-pieces";
import { useFilterPreferencesSync } from "../components/document-table/use-filter-preferences-sync";
import { IfcViewer } from "../components/ifc-viewer";

type ExtractedDocument = {
  versionId: number;
  number: string;
  name: string;
  index: string | null;
  fichierPdfId: number | null;
  fichierNativeId: number | null;
};

type BimVersion = {
  id: number;
  index: string | null;
  createdAt: string;
  fichierIfcId: number | null;
  fichierNativeId: number | null;
  recipientCount: number;
  documents: ExtractedDocument[];
  awaitingDiffusion: boolean;
};

type BimModel = {
  id: number;
  number: string | null;
  name: string;
  zones: string[];
  performances: string[];
  versions: BimVersion[];
};

type BimAuthor = {
  contractId: number;
  name: string;
  lots: Array<{
    id: number | null;
    number: string | null;
    name: string;
    models: BimModel[];
  }>;
};

/** Maquette version being put in diffusion ("Mettre en diffusion"). */
type DiffusionState = {
  modelId: number;
  versionId: number;
  name: string;
  index: string | null;
};

type FetchState =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "success";
      authors: BimAuthor[];
      canFilterCoordination: boolean;
    };

export function ModelesBIMConception() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <h2 className="mb-4 text-xl font-semibold text-zinc-900 dark:text-zinc-100">
        Modèles BIM conception
      </h2>
      <div className="rounded-lg border border-zinc-200 bg-white p-6 text-center dark:border-zinc-700 dark:bg-zinc-900">
        <p className="text-sm text-zinc-500">En cours de développement</p>
      </div>
    </div>
  );
}

export function ModelesBIM() {
  const { yardId } = useParams<{ yardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [diffusion, setDiffusion] = useState<DiffusionState | null>(null);
  const [diffusionLoading, setDiffusionLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [ifcViewer, setIfcViewer] = useState<{
    fichierIfcId: number;
    title: string;
  } | null>(null);
  const [extracted, setExtracted] = useState<{
    maquette: string;
    documents: ExtractedDocument[];
  } | null>(null);
  const { hasContractType } = useYardContext();
  // Same rule as the documents list: a works contract diffuses; the API
  // further checks that the caller pilots the maquette.
  const canDiffuse =
    hasContractType("travaux_n_1") || hasContractType("travaux_n_2");

  const filters = readBimModelFilters(searchParams);
  const indexMode = filters.indexMode.find(
    (mode): mode is "recent3" | "coordination" =>
      mode === "recent3" || mode === "coordination",
  );
  const { hideObsolete, needsUpdate } = filters;
  const hasActiveFilters = hasActiveBimModelFilters(filters);

  const applyFilters = useCallback(
    (next: BimModelListFilters) =>
      setSearchParams(bimModelFiltersToSearchParams(next), { replace: true }),
    [setSearchParams],
  );
  useFilterPreferencesSync(
    yardId,
    filters,
    searchParams.toString(),
    hasActiveFilters,
    applyFilters,
    bimModelFilterPreferences,
  );
  const dropIndexMode = useEffectEvent(() =>
    applyFilters({ ...filters, indexMode: [] }),
  );

  useEffect(() => {
    if (isNil(yardId)) return;
    let cancelled = false;

    async function loadModels() {
      setState((previous) =>
        previous.status === "success" ? previous : { status: "loading" },
      );
      const { data, error, response } = await apiClient.GET(
        "/api/yards/{yardId}/bim-models",
        {
          params: {
            path: { yardId: yardId ?? "" },
            query: {
              indexMode,
              hideObsolete: hideObsolete ? "1" : undefined,
              needsUpdate: needsUpdate ? "1" : undefined,
            },
          },
        },
      );
      if (cancelled) return;
      if (error || isNil(data)) {
        if (indexMode === "coordination" && response.status === 403) {
          dropIndexMode();
          return;
        }
        setState({ status: "error" });
        return;
      }
      setState({ status: "success", ...data.data });
    }

    void loadModels();
    return () => {
      cancelled = true;
    };
  }, [yardId, indexMode, hideObsolete, needsUpdate, reloadKey]);

  const download = useCallback(
    async (fileId: number) => {
      if (isNil(yardId)) return;
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fileId) } } },
      );
      if (error || isNil(data)) {
        toaster.error({
          title: "Téléchargement impossible",
          description:
            error?.error ?? "Le lien du fichier n'a pas pu être obtenu.",
        });
        return;
      }
      window.open(data.data.url, "_blank", "noopener,noreferrer");
    },
    [yardId],
  );

  const confirmDiffusion = async (
    targets: DiffusionTarget[],
    avisRequis: boolean,
  ) => {
    if (isNil(yardId) || isNil(diffusion)) return;
    setDiffusionLoading(true);
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/bim-models/{bimModelId}/versions/{versionId}/diffusion",
        {
          params: {
            path: {
              yardId,
              bimModelId: diffusion.modelId,
              versionId: diffusion.versionId,
            },
          },
          body: { targets, avisRequis },
        },
      );
      if (isNotNil(error)) {
        toaster.error({
          title: "Maquette non ajoutée à la liste de diffusion",
          description: error.error,
        });
        return;
      }
      toaster.success({ title: "Modifications enregistrées" });
      setDiffusion(null);
      setReloadKey((key) => key + 1);
    } catch (err) {
      console.error("Failed to add BIM model to diffusion", err);
      captureException(err);
      toaster.error({
        title: "Maquette non ajoutée à la liste de diffusion",
        description: "Le serveur n'a pas pu être joint. Réessayez.",
      });
    } finally {
      setDiffusionLoading(false);
    }
  };

  const visibleAuthors =
    state.status === "success"
      ? filterBimModelTree(state.authors, filters)
      : [];
  const modelCount = new Set(
    visibleAuthors.flatMap((author) =>
      author.lots.flatMap((lot) => lot.models.map((model) => model.id)),
    ),
  ).size;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="shrink-0 border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Modèles BIM d'exécution
        </h2>
        {state.status === "success" && (
          <p className="mt-1 text-sm text-zinc-500">
            {modelCount} {modelCount > 1 ? "maquettes" : "maquette"}
          </p>
        )}
      </header>

      {state.status === "success" &&
        (state.authors.length > 0 || hasActiveFilters) && (
          <BimModelFilterBar
            authors={state.authors}
            filters={filters}
            hasWorksContract={state.canFilterCoordination}
            onChange={applyFilters}
          />
        )}

      <div className="flex min-h-0 flex-1 flex-col px-4 py-4">
        {state.status === "loading" ? (
          <p className="py-12 text-center text-sm text-zinc-500">
            Chargement des maquettes…
          </p>
        ) : state.status === "error" ? (
          <p className="py-12 text-center text-sm text-red-600">
            Impossible de charger les maquettes.
          </p>
        ) : state.authors.length === 0 && !hasActiveFilters ? (
          <p className="py-12 text-center text-sm text-zinc-500">
            Aucune maquette d'exécution pour ce chantier.
          </p>
        ) : visibleAuthors.length === 0 ? (
          <p className="py-12 text-center text-sm text-zinc-500">
            Aucune maquette ne correspond aux filtres.
          </p>
        ) : (
          <div className="min-h-0 overflow-y-auto overflow-x-hidden rounded-lg border border-zinc-200 dark:border-zinc-700">
            <table className="w-full table-fixed border-collapse text-sm">
              {/* Variable columns in plain percentages: Chrome and WebKit
                  ignore calc() widths on <col>. */}
              <colgroup>
                <col className="w-[15%]" />
                <col className="w-[11%]" />
                <col className="w-[19%]" />
                <col className="w-[13%]" />
                <col className="w-20" />
                <col className="w-28" />
                <col className="w-24" />
                <col className="w-32" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-800">
                <tr className="h-10 border-b border-zinc-200 dark:border-zinc-700">
                  <th className="px-3 text-center font-medium">Prestation</th>
                  <th className="px-3 text-center font-medium">N°</th>
                  <th className="px-3 text-center font-medium">Intitulé</th>
                  <th className="px-3 text-center font-medium">
                    Niveaux / Façades
                  </th>
                  <th className="px-2 text-center font-medium">Indice</th>
                  <th className="px-2 text-center font-medium">Formats</th>
                  <th className="px-2 text-center font-medium leading-4">
                    Documents extraits
                  </th>
                  <th className="px-2 text-center font-medium">
                    Destinataires
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleAuthors.map((author) => (
                  <Fragment key={author.contractId}>
                    <tr className="h-10 border-b border-zinc-200 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800/70">
                      <th colSpan={8} className="px-3 text-left font-semibold">
                        Auteur — {author.name}
                      </th>
                    </tr>
                    {author.lots.map((lot) => (
                      <Fragment key={lot.id ?? "none"}>
                        <tr className="h-10 border-b border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/30">
                          <th
                            colSpan={8}
                            className="px-3 text-left font-medium"
                          >
                            {lot.number ? `Lot ${lot.number} — ` : ""}
                            {lot.name}
                          </th>
                        </tr>
                        {lot.models.map((model) => (
                          <ModelRows
                            key={model.id}
                            model={model}
                            onDownload={download}
                            onOpenIfc={setIfcViewer}
                            onOpenDocuments={(version) =>
                              setExtracted({
                                maquette: maquetteVersionLabel({
                                  ...model,
                                  index: version.index,
                                }),
                                documents: version.documents,
                              })
                            }
                            onDiffuse={
                              canDiffuse
                                ? (version) =>
                                    setDiffusion({
                                      modelId: model.id,
                                      versionId: version.id,
                                      name: isNil(model.number)
                                        ? model.name
                                        : `${model.number} - ${model.name}`,
                                      index: version.index,
                                    })
                                : undefined
                            }
                          />
                        ))}
                      </Fragment>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <DiffusionDialog
        open={isNotNil(diffusion)}
        onClose={() => setDiffusion(null)}
        documentName={diffusion?.name ?? ""}
        versionIndex={diffusion?.index ?? null}
        onConfirm={confirmDiffusion}
        loading={diffusionLoading}
      />
      <ExtractedDocumentsPopup
        extracted={extracted}
        onClose={() => setExtracted(null)}
        onOpenPdf={(fileId) => {
          if (isNotNil(yardId)) void openFichier(yardId, fileId);
        }}
        onDownload={download}
      />
      {isNotNil(yardId) && isNotNil(ifcViewer) && (
        <IfcViewer
          yardId={yardId}
          fichierIfcId={ifcViewer.fichierIfcId}
          title={ifcViewer.title}
          onDownload={download}
          onClose={() => setIfcViewer(null)}
        />
      )}
    </div>
  );
}

function ModelRows({
  model,
  onDownload,
  onOpenIfc,
  onOpenDocuments,
  onDiffuse,
}: {
  model: BimModel;
  onDownload: (fileId: number) => void;
  onOpenIfc: (viewer: { fichierIfcId: number; title: string }) => void;
  onOpenDocuments: (version: BimVersion) => void;
  onDiffuse?: (version: BimVersion) => void;
}) {
  return model.versions.map((version, index) => (
    <tr
      key={version.id}
      className="h-10 border-b border-zinc-100 last:border-b-0 dark:border-zinc-800"
    >
      {index === 0 && (
        <>
          <ModelCell
            value={model.performances.join(", ") || "—"}
            rowSpan={model.versions.length}
          />
          <ModelCell
            value={model.number ?? "—"}
            rowSpan={model.versions.length}
            mono
          />
          <ModelCell value={model.name} rowSpan={model.versions.length} />
          <ModelCell
            value={model.zones.join(", ") || "—"}
            rowSpan={model.versions.length}
          />
        </>
      )}
      <td className="px-2 text-center" title={version.index ?? "Sans indice"}>
        {version.index ?? "—"}
        {version.awaitingDiffusion && <AwaitingDiffusionLabel />}
      </td>
      <td className="px-2 text-center">
        <div className="flex items-center justify-center gap-2">
          <DownloadButton
            fileId={version.fichierIfcId}
            label={`Voir la maquette IFC de la version ${version.index ?? "sans indice"}`}
            title="Voir la maquette IFC"
            onDownload={(fichierIfcId) =>
              onOpenIfc({
                fichierIfcId,
                title: maquetteVersionLabel({ ...model, index: version.index }),
              })
            }
          >
            <CubeIcon size={18} />
          </DownloadButton>
          <DownloadButton
            fileId={version.fichierNativeId}
            label={`Télécharger le fichier natif de la version ${version.index ?? "sans indice"}`}
            title="Télécharger le fichier natif"
            onDownload={onDownload}
          >
            <FileArrowDownIcon size={18} />
          </DownloadButton>
          {isNil(version.fichierIfcId) && isNil(version.fichierNativeId) && "—"}
        </div>
      </td>
      <td className="px-2 text-center">
        {version.documents.length > 0 ? (
          <button
            type="button"
            onClick={() => onOpenDocuments(version)}
            className="text-blue-600 hover:underline dark:text-blue-400"
            title="Voir les documents extraits"
            aria-label={`${version.documents.length} doc extraits de la version ${version.index ?? "sans indice"}`}
          >
            {version.documents.length} doc
          </button>
        ) : (
          "—"
        )}
      </td>
      <td
        className="px-2 text-center"
        title={`${version.recipientCount} destinataire(s)`}
      >
        <span className="inline-flex items-center justify-center gap-1">
          <UsersIcon size={17} />
          {version.recipientCount}
        </span>
        {/* A version saved without any file has nothing to diffuse. */}
        {isNotNil(onDiffuse) &&
          (isNotNil(version.fichierIfcId) ||
            isNotNil(version.fichierNativeId)) && (
            <button
              type="button"
              onClick={() => onDiffuse(version)}
              className="ml-2 align-middle text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400"
              title="Diffuser"
              aria-label={`Diffuser la version ${version.index ?? "sans indice"}`}
            >
              <PaperPlaneIcon size={16} />
            </button>
          )}
      </td>
    </tr>
  ));
}

/** Document versions extracted from a maquette version (N18). */
function ExtractedDocumentsPopup({
  extracted,
  onClose,
  onOpenPdf,
  onDownload,
}: {
  extracted: { maquette: string; documents: ExtractedDocument[] } | null;
  onClose: () => void;
  onOpenPdf: (fileId: number) => void;
  onDownload: (fileId: number) => void;
}) {
  return (
    <Modal.Root
      open={isNotNil(extracted)}
      onOpenChange={(e) => !e.open && onClose()}
    >
      <Modal.Backdrop />
      <Modal.Positioner>
        <Modal.Content size="xl">
          <Modal.Header>
            <Modal.Title>Documents extraits</Modal.Title>
          </Modal.Header>
          <div className="px-6 pb-6">
            <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
              {extracted?.maquette}
            </p>
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full table-fixed border-collapse text-sm">
                <colgroup>
                  <col className="w-32" />
                  <col />
                  <col className="w-16" />
                  <col className="w-20" />
                </colgroup>
                <thead>
                  <tr className="h-10 border-b border-zinc-200 dark:border-zinc-700">
                    <th className="px-3 text-left font-medium">N°</th>
                    <th className="px-3 text-left font-medium">Intitulé</th>
                    <th className="px-2 text-center font-medium">Indice</th>
                    <th className="px-2 text-center font-medium">Formats</th>
                  </tr>
                </thead>
                <tbody>
                  {extracted?.documents.map((doc) => (
                    <tr
                      key={doc.versionId}
                      className="h-10 border-b border-zinc-100 dark:border-zinc-800"
                    >
                      <td className="truncate px-3 font-mono text-xs">
                        {doc.number}
                      </td>
                      <td className="truncate px-3" title={doc.name}>
                        {doc.name}
                      </td>
                      <td className="px-2 text-center">{doc.index ?? "—"}</td>
                      <td className="px-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <DownloadButton
                            fileId={doc.fichierPdfId}
                            label={`Voir le PDF de ${doc.number} indice ${doc.index ?? "sans indice"}`}
                            title="Voir le PDF"
                            className="text-red-600 hover:text-red-700 dark:text-red-400"
                            onDownload={onOpenPdf}
                          >
                            <FilePdfIcon size={18} weight="fill" />
                          </DownloadButton>
                          <DownloadButton
                            fileId={doc.fichierNativeId}
                            label={`Télécharger le fichier natif de ${doc.number} indice ${doc.index ?? "sans indice"}`}
                            title="Télécharger le fichier natif"
                            onDownload={onDownload}
                          >
                            <FileArrowDownIcon size={18} />
                          </DownloadButton>
                          {isNil(doc.fichierPdfId) &&
                            isNil(doc.fichierNativeId) &&
                            "—"}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Modal.Footer>
            <Button onClick={onClose}>Fermer</Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Positioner>
    </Modal.Root>
  );
}

function DownloadButton({
  fileId,
  label,
  title,
  className = "text-blue-600 hover:text-blue-800 dark:text-blue-400",
  onDownload,
  children,
}: {
  fileId: number | null;
  label: string;
  title: string;
  className?: string;
  onDownload: (fileId: number) => void;
  children: ReactNode;
}) {
  if (isNil(fileId)) return null;
  return (
    <button
      type="button"
      onClick={() => onDownload(fileId)}
      className={className}
      title={title}
      aria-label={label}
    >
      {children}
    </button>
  );
}

function ModelCell({
  value,
  rowSpan,
  mono = false,
}: {
  value: string;
  rowSpan: number;
  mono?: boolean;
}) {
  return (
    <td
      rowSpan={rowSpan}
      className="border-r border-zinc-100 px-3 text-center align-middle dark:border-zinc-800"
    >
      <div
        className={mono ? "truncate font-mono text-xs" : "truncate"}
        title={value}
      >
        {value}
      </div>
    </td>
  );
}
